/**
 * zero-app, client-side Wishlist System for Shopify
 * Manages items in localStorage, syncs counts, and renders the wishlist page.
 */

// Wrap in typeof guard to prevent syntax re-declaration errors if script is loaded twice
if (typeof window.ShopifyWishlist === 'undefined') {
  const ShopifyWishlist = (function() {
    const LOCAL_STORAGE_KEY = 'indian_vastra_wishlist';
    const EVENT_UPDATED = 'wishlist:updated';

    // Retrieve wishlist items from localStorage
    function getItems() {
      try {
        return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [];
      } catch (e) {
        console.error('Failed to parse wishlist from localStorage', e);
        return [];
      }
    }

    // Save wishlist items to localStorage
    function saveItems(items) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
      // Dispatch custom event to notify other scripts
      document.dispatchEvent(new CustomEvent(EVENT_UPDATED, { detail: items }));
      updateHeaderCount();
    }

    // Toggle product in wishlist
    function toggleItem(item) {
      let items = getItems();
      const index = items.findIndex(i => i.handle === item.handle);

      if (index > -1) {
        // Remove item
        items.splice(index, 1);
        saveItems(items);
        return false; // Removed
      } else {
        // Add item
        items.push(item);
        saveItems(items);
        return true; // Added
      }
    }

    // Check if an item is in the wishlist
    function hasItem(handle) {
      const items = getItems();
      return items.some(i => i.handle === handle);
    }

    // Find header wishlist link and inject/update count bubble
    function updateHeaderCount() {
      const items = getItems();
      const count = items.length;

      // Search for wishlist links in header (switched to favorites to bypass Swym app hijacking)
      const wishlistLinks = document.querySelectorAll('a[href*="/pages/favorites"], a[href*="/favorites"], .wishlist-header-link');
      
      wishlistLinks.forEach(link => {
        let badge = link.querySelector('.custom-favs-count, .wishlist-count-badge');
        
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'cart-count custom-favs-count';
          link.style.position = 'relative';
          link.appendChild(badge);
        }

        badge.textContent = count;

        if (count > 0) {
          badge.style.display = 'flex';
        } else {
          badge.style.display = 'none';
        }
      });
    }

    // Sync button active states on page load
    function syncButtons() {
      const buttons = document.querySelectorAll('.wishlist-toggle-btn');
      buttons.forEach(btn => {
        const handle = btn.getAttribute('data-handle');
        if (handle && hasItem(handle)) {
          btn.classList.add('is-active');
        } else {
          btn.classList.remove('is-active');
        }
      });
    }

    // Set up click listeners on wishlist buttons
    function bindButtons() {
      document.body.addEventListener('click', function(e) {
        const btn = e.target.closest('.wishlist-toggle-btn');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();

        console.log('Wishlist Toggle Clicked!', btn);

        // Gather item data from button attributes
        const handle = btn.getAttribute('data-handle');
        const id = btn.getAttribute('data-variant-id') || btn.getAttribute('data-product-id');
        const title = btn.getAttribute('data-title');
        const price = btn.getAttribute('data-price');
        const image = btn.getAttribute('data-image');
        const url = btn.getAttribute('data-url');
        
        console.log('Extracted Item Details:', { handle, id, title, price, image, url });

        // Get selected size/color if available
        let options = '';
        const card = btn.closest('.product-card');
        if (card) {
          const activeSize = card.querySelector('.size-chip.selected');
          if (activeSize) {
            options = `Size: ${activeSize.getAttribute('data-size-value')}`;
          }
        }

        if (!handle) {
          console.warn('Wishlist add aborted: "data-handle" attribute is missing on the button!');
          return;
        }

        const item = {
          id: id,
          handle: handle,
          title: title || 'Product',
          price: price || '',
          image: image || '',
          url: url || `/products/${handle}`,
          options: options
        };

        const added = toggleItem(item);
        console.log('Wishlist status updated:', added ? 'Added' : 'Removed', item);
        
        // Update this button and any duplicate buttons for the same product
        const siblingBtns = document.querySelectorAll(`.wishlist-toggle-btn[data-handle="${handle}"]`);
        siblingBtns.forEach(b => {
          if (added) {
            b.classList.add('is-active');
          } else {
            b.classList.remove('is-active');
          }
        });
      });
    }

    // Initialize wishlist functions
    function init() {
      // Inject count badge CSS globally
      if (!document.getElementById('wishlist-badge-global-style')) {
        const style = document.createElement('style');
        style.id = 'wishlist-badge-global-style';
        style.textContent = `
          .wishlist-count-badge {
            position: absolute;
            top: -7px;
            right: -7px;
            background-color: #8A1538;
            color: #ffffff;
            font-size: 9px;
            font-weight: 700;
            width: 15px;
            height: 15px;
            border-radius: 50%;
            display: none;
            align-items: center;
            justify-content: center;
            border: 1.5px solid #ffffff;
            z-index: 15;
            font-family: 'Inter', sans-serif;
            line-height: 1;
            box-shadow: 0 2px 5px rgba(0,0,0,0.15);
          }
        `;
        document.head.appendChild(style);
      }

      bindButtons();
      syncButtons();
      updateHeaderCount();

      // Force redirection capture handler
      document.addEventListener('click', function(e) {
        const link = e.target.closest('a[href*="/pages/favorites"]');
        if (link) {
          if (!window.Shopify || !window.Shopify.designMode) {
            e.preventDefault();
            window.location.href = link.href;
          }
        }
      }, true);
      
      // Bind size chip switches
      document.body.addEventListener('click', function(e) {
        const chip = e.target.closest('.size-chip');
        if (!chip) return;

        const card = chip.closest('.product-card');
        if (!card) return;

        const wishlistBtn = card.querySelector('.wishlist-toggle-btn');
        const variantIdInput = card.querySelector('input[name="id"]');

        if (wishlistBtn && variantIdInput) {
          wishlistBtn.setAttribute('data-variant-id', variantIdInput.value);
          wishlistBtn.setAttribute('data-image', card.querySelector('.product-image.primary')?.src || '');
        }
      });
    }

    return {
      init: init,
      getItems: getItems,
      saveItems: saveItems,
      toggleItem: toggleItem,
      hasItem: hasItem,
      updateHeaderCount: updateHeaderCount,
      syncButtons: syncButtons
    };
  })();

  // Self initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ShopifyWishlist.init());
  } else {
    ShopifyWishlist.init();
  }
  window.ShopifyWishlist = ShopifyWishlist;
}